import React, { RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import classNames from "classnames";

// types
import { CouponMessageCard } from "@swiggy-private/cart-types";
import { OfferCardType } from "@swiggy-private/cart-types";
import { CouponPageType } from "@swiggy-private/checkout-core";

// dls
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";

// internal deps
import {
    AriaHidden,
    DialogHOC,
    isEmpty,
    ScreenReaderOnly,
    useEffectOnce,
    useFocusDialog,
    getActiveElement,
    getCloudinaryUrl,
} from "@swiggy-private/core-ui";

// constants
import { APPLY_COUPON_MODAL_TEST_IDS } from "@swiggy-private/cart-test-ids";
import {
    ICON_WIDTH,
    ACCESSIBILITY_TEXTS,
    PLUS_ICON,
    PLACEHOLDER_TEXT_FOR_IMG_INSERT,
    CART_FOOTER_ICON_WIDTH,
} from "./constants";

// widgets
import PlusIcon from "@cart-ui/widgets/plus-icon";

// helpers
import { getAriaLabel, getMoreOffersStripData } from "./helpers";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image as DLSImage } from "@swiggy-private/dls-web-core/dist/components/Image";

// styles
import S from "./styles/CartModal.scss";
import {
    CardFooterWrapper,
    CardFooterContentWrapper,
    CardFooterTextContainer,
    Container,
    StyledImage,
    CloseIconWrapper,
    Title,
    Subtitle,
    SecondarySavingsSubtitle,
    SecondaryDiscountCallout,
    Description,
    CTAWrapper,
    StyledSuperButton,
} from "./styles/CartModal.styles";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

export interface CartModalProps {
    data: CouponMessageCard;
    closeModal: VoidFunction;
    isNewPORFlow?: boolean;
    goToCouponListing?: (couponListingType: CouponPageType) => void;
    onImpressionMoreOffersStrip?: (data: Record<string, unknown>) => void;
    onClickMoreOffersStrip?: VoidFunction;
    onClickCouponSavingsPopupWithPOR?: (text: string) => void;
}

const CartModal: React.FC<CartModalProps> = ({
    data,
    closeModal,
    isNewPORFlow,
    goToCouponListing,
    onImpressionMoreOffersStrip,
    onClickMoreOffersStrip,
    onClickCouponSavingsPopupWithPOR,
}: CartModalProps) => {
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);
    const primarySavingsText = data.primarySavingsInfo?.text || data.text;
    const primarySavingsSubtext = data.primarySavingsInfo?.subtext || data.subtext;
    const primaryTextToShowInMiddle = `${primarySavingsText ?? ""} ${primarySavingsSubtext ?? ""}`.trim();
    const secondarySavingsText = data.secondarySavingsInfo?.text;
    const secondarySavingsSubtext = data.secondarySavingsInfo?.subtext;
    const secondaryTextToShowInMiddle = `${secondarySavingsText ?? ""} ${secondarySavingsSubtext ?? ""}`.trim();
    useEffectOnce(() => {
        previouslyFocusedElement.current = getActiveElement();
    });

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    const cartModalAriaLabel = useMemo(
        () => getAriaLabel(primaryTextToShowInMiddle, data),
        [data, primaryTextToShowInMiddle],
    );

    // check if an image is provided, check if the primary text has the placeholder text for image insertion
    // if both are true, then insert the image html inside the primary text html
    const [finalPrimaryText, isHTMLInsertionNeeded] = useMemo(() => {
        if (
            !isEmpty(data?.attributeValues?.logo) &&
            !isEmpty(primaryTextToShowInMiddle) &&
            primaryTextToShowInMiddle.includes(PLACEHOLDER_TEXT_FOR_IMG_INSERT)
        ) {
            const imageHTMLStr = `<div class=${classNames(S.imageInsertedInsidePrimaryText)}><img class=${classNames(
                S.imageInsertedInsidePrimaryTextIcon,
            )} src="${getCloudinaryUrl(data?.attributeValues?.logo)}" data-testid="${
                APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_IMG
            }"/></div>`;
            return [
                `<div>${primaryTextToShowInMiddle.replace(PLACEHOLDER_TEXT_FOR_IMG_INSERT, imageHTMLStr)}</div>`,
                true,
            ];
        }
        return [primaryTextToShowInMiddle, false];
    }, [data?.attributeValues, primaryTextToShowInMiddle]);

    const isPHApplyCoupon = data?.offer_card_type === OfferCardType.OFFER_CARD_TYPE_POCKET_HERO;

    const plusIconStyle = classNames(S.plusIcon, {
        [S.plusIconNewPORFlow]: isNewPORFlow,
    });

    const shouldShowCardFooter = useMemo(
        () => !isEmpty(data.card_footer) && isNewPORFlow,
        [data.card_footer, isNewPORFlow],
    );

    const handleCloseModal = useCallback(() => {
        if (isNewPORFlow && typeof onClickCouponSavingsPopupWithPOR === "function") {
            onClickCouponSavingsPopupWithPOR(cartModalAriaLabel);
        }
        closeModal();
    }, [isNewPORFlow, onClickCouponSavingsPopupWithPOR, closeModal, cartModalAriaLabel]);

    const handleCardFooterClick = useCallback(() => {
        handleCloseModal();

        if (typeof onClickMoreOffersStrip === "function") {
            onClickMoreOffersStrip();
        }

        if (typeof goToCouponListing === "function") {
            goToCouponListing(CouponPageType.PAYMENT_COUPON_LISTING);
        }
    }, [handleCloseModal, goToCouponListing, onClickMoreOffersStrip]);

    useEffect(() => {
        if (shouldShowCardFooter && typeof onImpressionMoreOffersStrip === "function") {
            onImpressionMoreOffersStrip(getMoreOffersStripData(data));
        }
    }, [shouldShowCardFooter, onImpressionMoreOffersStrip, data]);

    return (
        <Container
            aria-modal="true"
            data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL}
            isPHApplyCoupon={isPHApplyCoupon}
            isNewPORFlow={isNewPORFlow}
            showingCardFooter={shouldShowCardFooter}
        >
            <AriaHidden as="div">
                {!isEmpty(data.icon) && (
                    <StyledImage
                        cloudinaryId={data.icon}
                        lazyLoad={false}
                        width={(!isPHApplyCoupon && `${ICON_WIDTH * 2}px`) || undefined}
                        height="48px"
                    />
                )}
                <CloseIconWrapper onClick={handleCloseModal}>
                    <SpriteIconWeb iconName="clearFilled24" fillColor="text_Low_Emphasis" />
                </CloseIconWrapper>
                {/* always show when ux3 but for ux4 when there's no text in middle it looks weird,
             so we make this title text as the text to be shown in big font and not render this */}
                {primaryTextToShowInMiddle && !isPHApplyCoupon && (
                    <Title
                        variant="V2.body2.neutral.medium"
                        color="text_Low_Emphasis"
                        data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_TEXT}
                        dangerouslySetInnerHTML={{ __html: data.title }}
                    />
                )}
                {/* use case where image insertion is not needed, directly add text as the child for the parent div */}
                {(primaryTextToShowInMiddle || data.title) && !isHTMLInsertionNeeded && (
                    <Subtitle
                        variant="V2.heading1.neutral.black"
                        color="text_Highest_Emphasis"
                        noMarginTop={!primaryTextToShowInMiddle}
                        isNewPORFlow={isNewPORFlow}
                        data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_PRIMARY_TEXT}
                    >
                        {primaryTextToShowInMiddle || data.title}
                    </Subtitle>
                )}
                {/* use case where an image is provided in data for insertion into the primary text, 
                need to convert data into HTML string to have image html inline */}
                {(primaryTextToShowInMiddle || data.title) && isHTMLInsertionNeeded && (
                    <Subtitle
                        variant="V2.heading1.neutral.black"
                        color="text_Highest_Emphasis"
                        noMarginTop={!primaryTextToShowInMiddle}
                        dangerouslySetInnerHTML={{ __html: finalPrimaryText || data.title }}
                    />
                )}
                {!isEmpty(primaryTextToShowInMiddle) && !isEmpty(secondaryTextToShowInMiddle) && (
                    <>
                        <AriaHidden as="span" className={plusIconStyle}>
                            <PlusIcon iconFillColor={PLUS_ICON.fill} />
                        </AriaHidden>
                        <SecondarySavingsSubtitle variant="V2.heading1.neutral.black" color="text_Highest_Emphasis">
                            {secondaryTextToShowInMiddle}
                        </SecondarySavingsSubtitle>
                    </>
                )}
                {!isEmpty(primaryTextToShowInMiddle) && !isEmpty(data.secondaryDiscountCallout) && (
                    <SecondaryDiscountCallout variant="V2.subheading1.neutral.bold" color="text_Med_Emphasis">
                        {data.secondaryDiscountCallout}
                    </SecondaryDiscountCallout>
                )}
                {!isEmpty(data.description) && (
                    <Description
                        variant="V2.body2.neutral.medium"
                        color="text_Low_Emphasis"
                        isNewPORFlow={isNewPORFlow}
                        data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_DESCRIPTION}
                        dangerouslySetInnerHTML={{ __html: data.description }}
                    />
                )}
            </AriaHidden>
            <ScreenReaderOnly text={cartModalAriaLabel} as="div" compRef={headerDivRef as RefObject<HTMLDivElement>} />
            <CTAWrapper isNewPORFlow={isNewPORFlow}>
                <StyledSuperButton
                    buttonType="primary"
                    buttonSize="medium"
                    variant="fullwidth"
                    onClick={handleCloseModal}
                    data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_CLOSE}
                    aria-label={ACCESSIBILITY_TEXTS.GO_BACK_TO_CART}
                    backgroundColor={data?.cta?.color as keyof IColors}
                    isCustomButton={data?.cta?.isCustomCTA ?? false}
                >
                    {data?.cta?.isCustomCTA ? (
                        <Text variant="V2.body2.neutral.bold" color={data?.cta?.textColor as keyof IColors}>
                            {data?.cta?.text}
                        </Text>
                    ) : (
                        data?.cta?.text
                    )}
                </StyledSuperButton>
            </CTAWrapper>
            {shouldShowCardFooter && (
                <CardFooterWrapper
                    data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_CARD_FOOTER}
                    onClick={handleCardFooterClick}
                >
                    <CardFooterContentWrapper>
                        <DLSImage
                            alt={"PAYMENT_LOGO"}
                            cloudinaryId={data.card_footer?.icon}
                            width={CART_FOOTER_ICON_WIDTH}
                            fit="contain"
                        />
                        <CardFooterTextContainer>
                            <Text
                                variant="V2.body3.neutral.bold"
                                color="text_Highest_Emphasis"
                                data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_CARD_FOOTER_TEXT}
                            >
                                {data.card_footer?.text ?? ""}
                            </Text>
                            <Text
                                as="span"
                                variant="V2.overline.neutral.regular"
                                color="text_Med_Emphasis"
                                data-testid={APPLY_COUPON_MODAL_TEST_IDS.CART_APPLY_COUPON_MODAL_CARD_FOOTER_SUBTEXT}
                            >
                                {data.card_footer?.subtext ?? ""}
                            </Text>
                        </CardFooterTextContainer>
                    </CardFooterContentWrapper>
                    <SpriteIconWeb
                        iconName="arrowFront16"
                        fillColor="text_Med_Emphasis"
                        strokeColor="text_Med_Emphasis"
                    />
                </CardFooterWrapper>
            )}
        </Container>
    );
};

export default DialogHOC(React.memo(CartModal));
